import React, { useState, useEffect, useContext } from "react";
import { 
  Clipboard, Search, Filter, RefreshCw, User, Calendar, MapPin, 
  CheckCircle, Clock, AlertTriangle, Play, X 
} from "lucide-react";
import { employeeWorkforceAPI, staffAPI } from "../../api/apiService";
import { NotificationContext } from "../../context/NotificationContext";
import { motion } from "framer-motion";
import EmptyState from "../../components/EmptyState";

export default function AttendanceReports() {
  const { addNotification } = useContext(NotificationContext);
  const [attendanceList, setAttendanceList] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewSelfie, setPreviewSelfie] = useState(null);

  // Filters
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [selectedDate, setSelectedDate] = useState("");

  const fetchData = async () => {
    setLoading(true);
    try {
      const [attRes, empRes] = await Promise.all([
        employeeWorkforceAPI.getAttendanceLogs(),
        staffAPI.getAllStaff()
      ]);
      setAttendanceList(attRes.data);
      setEmployees(empRes.data);
    } catch (err) {
      console.error(err);
      addNotification("Failed to fetch attendance reports", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const filteredAttendance = attendanceList.filter(item => {
    const matchEmp = selectedEmployee ? String(item.employee) === String(selectedEmployee) : true;
    const matchStatus = selectedStatus ? item.status === selectedStatus : true;
    const matchDate = selectedDate ? item.date === selectedDate : true;
    return matchEmp && matchStatus && matchDate;
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-slate-900">
        <EmptyState
          preset="verification"
          title="Loading Attendance Reports..."
          message="Please wait a moment while we load logs."
        />
      </div>
    );
  }

  const statusBadges = {
    "ON TIME": "bg-green-50 text-green-600 dark:bg-green-950/30 dark:text-green-400 border border-green-200 dark:border-green-800",
    "LATE": "bg-amber-50 text-amber-600 dark:bg-amber-950/30 dark:text-amber-400 border border-amber-200 dark:border-amber-800",
    "FIELD VISIT": "bg-blue-50 text-blue-600 dark:bg-blue-950/30 dark:text-blue-400 border border-blue-200 dark:border-blue-800",
    "HALF DAY": "bg-orange-50 text-orange-600 dark:bg-orange-950/30 dark:text-orange-400 border border-orange-200 dark:border-orange-800",
    "ABSENT": "bg-red-50 text-red-600 dark:bg-red-950/30 dark:text-red-400 border border-red-200 dark:border-red-800",
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900 p-4 md:p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        
        {/* Header */}
        <div className="bg-white dark:bg-slate-800 p-6 md:p-8 rounded-3xl border border-gray-100 dark:border-slate-700 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-green-600 rounded-2xl text-white shadow-lg shadow-green-500/20">
              <Clipboard className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-black text-gray-900 dark:text-white">Attendance Audits & Reports</h1>
              <p className="text-gray-500 dark:text-slate-400 font-medium">Verify employee punching entries, mapped coordinates, selfies, and field status</p>
            </div>
          </div>

          <button
            onClick={fetchData}
            className="flex items-center gap-2 px-5 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-2xl text-sm font-bold shadow-md shadow-green-500/10 active:scale-95 transition"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh Logs
          </button>
        </div>

        {/* Filters Group */}
        <div className="bg-white dark:bg-slate-800 p-6 rounded-3xl border border-gray-100 dark:border-slate-700 shadow-sm grid grid-cols-1 sm:grid-cols-3 gap-4">
          
          {/* Employee Filter */}
          <div>
            <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">Filter Employee</label>
            <select
              className="w-full px-4 py-3 bg-gray-50 dark:bg-slate-900 border border-gray-100 dark:border-slate-700 rounded-2xl focus:ring-2 focus:ring-green-500 transition dark:text-white appearance-none text-sm font-bold"
              value={selectedEmployee}
              onChange={(e) => setSelectedEmployee(e.target.value)}
            >
              <option value="">All Employees</option>
              {employees.map(emp => (
                <option key={emp.id} value={emp.id}>{emp.first_name} {emp.last_name || ""} ({emp.role})</option>
              ))}
            </select>
          </div>

          {/* Status Filter */}
          <div>
            <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">Filter Punch Status</label>
            <select
              className="w-full px-4 py-3 bg-gray-50 dark:bg-slate-900 border border-gray-100 dark:border-slate-700 rounded-2xl focus:ring-2 focus:ring-green-500 transition dark:text-white appearance-none text-sm font-bold"
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
            >
              <option value="">All Statuses</option>
              <option value="ON TIME">ON TIME</option>
              <option value="LATE">LATE</option>
              <option value="FIELD VISIT">FIELD VISIT</option>
              <option value="HALF DAY">HALF DAY</option>
              <option value="ABSENT">ABSENT</option>
            </select>
          </div>

          {/* Date Filter */}
          <div>
            <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">Filter Date</label>
            <input
              type="date"
              className="w-full px-4 py-2.5 bg-gray-50 dark:bg-slate-900 border border-gray-100 dark:border-slate-700 rounded-2xl focus:ring-2 focus:ring-green-500 transition text-sm dark:text-white font-bold"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
            />
          </div>

        </div>

        {/* Logs Table */}
        <div className="bg-white dark:bg-slate-800 rounded-3xl border border-gray-100 dark:border-slate-700 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-gray-50 dark:bg-slate-900/50 border-b border-gray-100 dark:border-slate-700">
                <tr>
                  <th className="px-6 py-4 text-xs font-bold uppercase text-gray-400 tracking-wider">Employee</th>
                  <th className="px-6 py-4 text-xs font-bold uppercase text-gray-400 tracking-wider">Date</th>
                  <th className="px-6 py-4 text-xs font-bold uppercase text-gray-400 tracking-wider">Status</th>
                  <th className="px-6 py-4 text-xs font-bold uppercase text-gray-400 tracking-wider">Check In</th>
                  <th className="px-6 py-4 text-xs font-bold uppercase text-gray-400 tracking-wider">Check Out</th>
                  <th className="px-6 py-4 text-xs font-bold uppercase text-gray-400 tracking-wider">Working Hours</th>
                  <th className="px-6 py-4 text-xs font-bold uppercase text-gray-400 tracking-wider text-right">Selfies</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-slate-700 text-sm">
                {filteredAttendance.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-12 text-center text-gray-400 font-bold">
                      No attendance logs match the current filters.
                    </td>
                  </tr>
                ) : filteredAttendance.map(item => {
                  const emp = employees.find(e => String(e.id) === String(item.employee)) || item.employee_details;
                  
                  return (
                    <tr key={item.id} className="hover:bg-gray-50/50 dark:hover:bg-slate-800/30 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-xl bg-green-500/10 text-green-600 flex items-center justify-center font-black text-sm">
                            {emp?.first_name?.[0] || "E"}{emp?.last_name?.[0] || ""}
                          </div>
                          <div>
                            <p className="font-bold text-gray-900 dark:text-white">
                              {emp ? `${emp.first_name} ${emp.last_name || ""}` : `Employee ID: ${item.employee}`}
                            </p>
                            <p className="text-[10px] text-gray-400 font-semibold tracking-wider uppercase mt-0.5">
                              ID: {emp?.salesman_id || "N/A"}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 font-bold text-gray-900 dark:text-white">
                        {new Date(item.date).toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" })}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider ${statusBadges[item.status]}`}>
                          {item.status}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-gray-800 dark:text-slate-200 font-semibold">
                          {item.punch_in_time ? new Date(item.punch_in_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : "N/A"}
                        </p>
                        {item.punch_in_address && (
                          <p className="text-[9px] text-gray-400 line-clamp-1 max-w-[150px] mt-0.5" title={item.punch_in_address}>
                            📍 {item.punch_in_address}
                          </p>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-gray-800 dark:text-slate-200 font-semibold">
                          {item.punch_out_time ? new Date(item.punch_out_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : "N/A"}
                        </p>
                        {item.punch_out_address && (
                          <p className="text-[9px] text-gray-400 line-clamp-1 max-w-[150px] mt-0.5" title={item.punch_out_address}>
                            📍 {item.punch_out_address}
                          </p>
                        )}
                      </td>
                      <td className="px-6 py-4 font-bold text-gray-950 dark:text-white">
                        {item.total_hours ? `${item.total_hours} hrs` : "N/A"}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex justify-end gap-2">
                          {item.punch_in_photo && (
                            <div className="relative group inline-block">
                              <img 
                                src={item.punch_in_photo} 
                                alt="Punch In Selfie" 
                                className="w-8 h-8 rounded-lg object-cover cursor-pointer border border-gray-200 hover:scale-110 active:scale-95 transition-all shadow-sm"
                                onClick={() => setPreviewSelfie({
                                  photo: item.punch_in_photo,
                                  employeeName: emp ? `${emp.first_name} ${emp.last_name || ""}` : `Employee ID: ${item.employee}`,
                                  employeeId: emp?.salesman_id || "N/A",
                                  date: item.date,
                                  time: item.punch_in_time,
                                  address: item.punch_in_address,
                                  latitude: item.punch_in_latitude,
                                  longitude: item.punch_in_longitude,
                                  type: "Punch In Check"
                                })}
                                title="Click to enlarge"
                              />
                              <div className="absolute right-10 bottom-0 scale-0 group-hover:scale-100 transition-all duration-200 z-50 bg-slate-900 p-2 rounded-xl shadow-2xl border border-slate-700 w-40 h-40 pointer-events-none">
                                <img src={item.punch_in_photo} alt="Punch In Expanded" className="w-full h-full object-contain" />
                              </div>
                            </div>
                          )}
                          {item.punch_out_photo && (
                            <div className="relative group inline-block">
                              <img 
                                src={item.punch_out_photo} 
                                alt="Punch Out Selfie" 
                                className="w-8 h-8 rounded-lg object-cover cursor-pointer border border-gray-200 hover:scale-110 active:scale-95 transition-all shadow-sm"
                                onClick={() => setPreviewSelfie({
                                  photo: item.punch_out_photo,
                                  employeeName: emp ? `${emp.first_name} ${emp.last_name || ""}` : `Employee ID: ${item.employee}`,
                                  employeeId: emp?.salesman_id || "N/A",
                                  date: item.date,
                                  time: item.punch_out_time,
                                  address: item.punch_out_address,
                                  latitude: item.punch_out_latitude,
                                  longitude: item.punch_out_longitude,
                                  type: "Punch Out Check"
                                })}
                                title="Click to enlarge"
                              />
                              <div className="absolute right-10 bottom-0 scale-0 group-hover:scale-100 transition-all duration-200 z-50 bg-slate-900 p-2 rounded-xl shadow-2xl border border-slate-700 w-40 h-40 pointer-events-none">
                                <img src={item.punch_out_photo} alt="Punch Out Expanded" className="w-full h-full object-contain" />
                              </div>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Lightbox / Image Preview Modal */}
      {previewSelfie && (
        <div 
          className="fixed inset-0 bg-black/90 backdrop-blur-sm z-[100] flex flex-col items-center justify-center p-4 md:p-6 cursor-zoom-out"
          onClick={() => setPreviewSelfie(null)}
        >
          <div 
            className="relative max-w-3xl max-h-[85vh] w-full flex flex-col items-center justify-center bg-slate-950 rounded-2xl overflow-hidden border border-slate-800 shadow-2xl cursor-default"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button on top-right */}
            <button 
              className="absolute top-4 right-4 text-white bg-black/60 hover:bg-black/80 p-2.5 rounded-full transition cursor-pointer z-50 shadow-lg border border-white/10"
              onClick={() => setPreviewSelfie(null)}
            >
              <X className="w-5 h-5" />
            </button>

            {/* Selfie Image */}
            <img 
              src={previewSelfie.photo} 
              alt="Enlarged Selfie" 
              className="w-full max-h-[80vh] object-contain"
            />

            {/* Details overlay bar on the bottom of the image itself */}
            <div className="absolute bottom-0 left-0 right-0 bg-black/80 backdrop-blur-md p-5 text-white border-t border-white/10 flex flex-col gap-2">
              <div className="flex items-center gap-3">
                <span className="px-2.5 py-0.5 bg-green-600 text-white rounded-full text-xs font-black uppercase tracking-wider">
                  {previewSelfie.type}
                </span>
                <span className="font-extrabold text-sm md:text-base">{previewSelfie.employeeName} (ID: {previewSelfie.employeeId})</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs md:text-sm font-semibold mt-1">
                <div className="space-y-1">
                  <div>
                    <span className="text-[10px] text-gray-400 font-bold uppercase block">Time & Date</span>
                    <span className="text-white text-sm md:text-base font-black">
                      {previewSelfie.time ? new Date(previewSelfie.time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : "N/A"}
                      <span className="ml-2 font-normal text-xs text-gray-300">
                        {previewSelfie.date ? new Date(previewSelfie.date).toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }) : "N/A"}
                      </span>
                    </span>
                  </div>
                  <div>
                    <span className="text-[10px] text-gray-400 font-bold uppercase block">Coordinates</span>
                    <span className="text-white font-mono">
                      Lat: {previewSelfie.latitude || "N/A"} | Lng: {previewSelfie.longitude || "N/A"}
                    </span>
                  </div>
                </div>
                <div>
                  <span className="text-[10px] text-gray-400 font-bold uppercase block">Location Address</span>
                  <span className="text-white text-xs leading-relaxed font-bold block mt-0.5">
                    📍 {previewSelfie.address || "No Address Logged"}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
